import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import jobPagesContext from '@/contexts/jobPagesContext';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import jobServices, { EditableJob } from '@/services/jobs';
import { Job, WorkingTime } from '@/types/api';
import { onScrollToSmoothly } from '@/utils/global';
import JobDetailsComponent from './JobDetails.component';
import { getFormattedCurrencyOriginalValue } from '../JobsList/JobsList.utils';

const JobDetails = function (): JSX.Element {
  const { jobId } = useParams();
  const toast = useToastContext();
  const { job, refetchJob, isLoadingJob } = useContext(jobPagesContext);

  const [updatedField, setUpdatedField] = useState('');
  const [workingTimes, setWorkingTimes] = useState<WorkingTime[]>([
    {
      start: '',
      end: '',
    },
  ]);

  const updateJobMutation = useApiSubmit<Job, any>(jobServices.updateJob);

  const editableJob = {
    ...job,
    techs: (job?.techs ?? []).map((tech) => tech?.id),
  };

  useEffect(() => {
    if (job?.workingTimes.length) {
      setWorkingTimes(job?.workingTimes);
    }
  }, [job?.workingTimes]);

  const sendNewJob = async (newJob: EditableJob): Promise<void> => {
    toast.setMessage('Saving information...');
    toast.setSeverity('info');
    toast.showToast();

    try {
      await updateJobMutation.mutateAsync(newJob);
      refetchJob();
      toast.showSuccessFeedback('The job information has been updated.');
    } catch (err) {
      toast.showErrorFeedback('There was an error saving the data.');
    }
  };

  const handleChangeWorkingTimes = async (
    event: ChangeEvent<HTMLInputElement>,
    index: number,
    field: 'start' | 'end'
  ): Promise<void> => {
    const currentWorkingTimes = cloneDeep(workingTimes);
    currentWorkingTimes[index][field] = event.target.value;

    setWorkingTimes(currentWorkingTimes);

    if (!!currentWorkingTimes[index].start && !!currentWorkingTimes[index].end) {
      await sendNewJob({
        id: jobId,
        workingTimes: currentWorkingTimes,
      });
    }
  };

  const onAddNewWorkingTime = (): void => {
    setWorkingTimes((prev) => [
      ...prev,
      {
        start: '',
        end: '',
      },
    ]);
  };

  const getExtraFields = (newJob: EditableJob, updatingField: string, newValue: any): any => {
    if (updatedField === 'techs') {
      return {
        ...newJob,
        skillIds: newValue,
      };
    }

    if (updatedField.includes('salaryRange')) {
      return {
        ...newJob,
        salaryRange: {
          [updatedField.replace('salaryRange', '').toLowerCase()]:
            getFormattedCurrencyOriginalValue(newValue),
        },
      };
    }

    return {
      ...newJob,
      [updatedField]: newValue,
    };
  };

  const onSubmitHandler = async (updatedJob: EditableJob): Promise<void> => {
    if (!updatedField) {
      return;
    }

    let newValue = updatedJob[updatedField];
    if (!!newValue && typeof newValue === 'string') {
      newValue = newValue.trim();
    }

    const newJob = {
      id: updatedJob.id,
    };

    await sendNewJob(getExtraFields(newJob, updatedField, newValue));
  };

  const onSaveText = async (field: string, value: string): Promise<void> => {
    await sendNewJob({
      id: jobId,
      [field]: value,
    });
  };

  const onSelectBenefit = async (benefitIds: string[]): Promise<void> => {
    await sendNewJob({
      id: jobId,
      benefitIds,
    });
  };

  if (isLoadingJob) {
    return <Loading fullPage />;
  }

  return (
    <JobDetailsComponent
      job={editableJob as any}
      workingTimes={workingTimes}
      handleChangeWorkingTimes={handleChangeWorkingTimes}
      onAddNewWorkingTime={onAddNewWorkingTime}
      onScrollTo={onScrollToSmoothly}
      updatedField={updatedField}
      onSubmit={onSubmitHandler}
      setUpdatedField={setUpdatedField}
      onSaveText={onSaveText}
      onSelectBenefit={onSelectBenefit}
    />
  );
};

export default JobDetails;
